// The Harvest Log form's hunt rules, shared by the web console and the Lodge
// iPhone app. The SERVER owns the lists (hunt types, their species, which use a
// method — GET /outfitter/harvest-log `huntTypes`); this only reads them, so
// the two clients cannot offer different species for the same hunt. The
// server's copy of the nearest-blind rule is api/src/outfitterHarvestLog.js
// (nearestLocations) — keep the two in lockstep.

export interface HuntTypeDef { code: string; label: string; usesMethod: boolean; species: string[]; unsexed?: string[] }
export interface PropertyOption { LocationID: string; Name: string; PropertyType?: string | null; Latitude?: number | null; Longitude?: number | null }
export interface PinnedLocation { HuntLocationID: string; Name: string; Latitude?: number | null; Longitude?: number | null; IsActive?: boolean | number }
export interface SexedRow { species: string; male: number; female: number }

export const huntTypeDef = (types: HuntTypeDef[] | undefined, code?: string | null): HuntTypeDef | null =>
  (types || []).find((t) => t.code === code) || null;

/** Method (gun type) is asked only for a hunt type that uses it (deer). No type picked: not asked. */
export const huntUsesMethod = (types: HuntTypeDef[] | undefined, code?: string | null): boolean =>
  !!huntTypeDef(types, code)?.usesMethod;

/** The species offered as one-tap picks for a hunt type; every type's list when none is picked. */
export function speciesForHunt(types: HuntTypeDef[] | undefined, code?: string | null, fallback: string[] = []): string[] {
  const t = huntTypeDef(types, code);
  if (t && t.species.length) return t.species;
  const all: string[] = [];
  for (const x of types || []) for (const n of x.species) if (!all.includes(n)) all.push(n);
  for (const n of fallback) if (!all.some((a) => a.toLowerCase() === n.toLowerCase())) all.push(n);
  return all;
}

/** The hunt types to show: only those the day's trips are booked for (plus the
 *  one already picked), unless the person expands the list or nothing is booked. */
export function visibleHuntTypes(types: HuntTypeDef[] | undefined, booked: string[] | undefined, picked: string | null | undefined, showAll: boolean):
  { shown: HuntTypeDef[]; hidden: number } {
  const all = types || [];
  const b = booked || [];
  if (showAll || !b.length) return { shown: all, hidden: 0 };
  const shown = all.filter((t) => b.includes(t.code) || t.code === picked);
  return { shown, hidden: all.length - shown.length };
}

/** How many of a hunt type's species the form lists before folding the rest behind "+ N more". */
export const SPECIES_SHOWN = 8;

/** A hunt type's species, most taken this season first (ties keep the list's own order). */
export function orderSpeciesBySeason(names: string[], bySpecies?: { species: string; count: number }[] | null): string[] {
  const seen = new Map((bySpecies || []).map((x) => [String(x.species).toLowerCase(), Number(x.count) || 0]));
  const at = new Map(names.map((n, i) => [n, i]));
  return [...names].sort((a, b) => (seen.get(b.toLowerCase()) || 0) - (seen.get(a.toLowerCase()) || 0) || (at.get(a)! - at.get(b)!));
}

/** A species counted with ONE number, not male + female (geese, doves, quail…). A typed species is sexed. */
export const isUnsexedSpecies = (types: HuntTypeDef[] | undefined, name: string): boolean =>
  (types || []).some((t) => (t.unsexed || []).some((n) => n.trim().toLowerCase() === String(name || "").trim().toLowerCase()));

export function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Active blinds that carry a GPS pin, nearest first, within maxMeters. */
export function nearestLocations<T extends PinnedLocation>(coords: { lat: number; lng: number } | null, locations: T[], limit = 3, maxMeters = 3000): { location: T; meters: number }[] {
  if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return [];
  return (locations || [])
    .filter((l) => (l.IsActive === undefined || !!l.IsActive) && l.Latitude != null && l.Longitude != null
      && Number.isFinite(Number(l.Latitude)) && Number.isFinite(Number(l.Longitude)))
    .map((l) => ({ location: l, meters: Math.round(distanceMeters(coords, { lat: Number(l.Latitude), lng: Number(l.Longitude) })) }))
    .filter((x) => x.meters <= maxMeters)
    .sort((a, b) => a.meters - b.meters)
    .slice(0, limit);
}

/** Properties (owned or leased) nearest a GPS reading — a property's pin is one point on a large tract, so the reach is wider than a blind's. */
export function nearestProperties<T extends PropertyOption>(coords: { lat: number; lng: number } | null, properties: T[], limit = 3, maxMeters = 15000): { property: T; meters: number }[] {
  const asLoc = (properties || []).map((p) => ({ HuntLocationID: p.LocationID, Name: p.Name, Latitude: p.Latitude, Longitude: p.Longitude, p }));
  return nearestLocations(coords, asLoc, limit, maxMeters).map((x) => ({ property: x.location.p, meters: x.meters }));
}
export const propertyLabel = (p: PropertyOption): string => (p.PropertyType === "leased" ? `${p.Name} (lease)` : p.Name);

export const fmtDistance = (m: number): string => (m < 1000 ? `${m} m` : `${(m / 1609.344).toFixed(1)} mi`);

/** "Mallard 3 (2M · 1F)"; a row logged before male/female were recorded shows its count only. */
export function speciesSummary(rows: { species: string; count: number; male?: number | null; female?: number | null }[] | undefined): string {
  return (rows || []).map((r) => (r.male != null || r.female != null
    ? `${r.species} ${r.count} (${r.male || 0}M · ${r.female || 0}F)` : `${r.species} ${r.count}`)).join(" · ");
}

export const sessionLabel = (s?: string | null): string => (s === "am" ? "AM" : s === "pm" ? "PM" : "");
